import { Inject, Injectable } from "@angular/core";
import { MessageService } from "primeng/api";
import { Store } from "@ngxs/store";
import { Observable, map } from "rxjs";
import { Campaign, CampaignCreateDto, CampaignUpdateDto } from "../models/emailcampaign";
import { CampaignStateActions } from "../state/email-campaigns/emailcampaign-actions";
import { ApiService } from "./api.service";
import { BASE_URL } from "./injection-tokens";

export abstract class EmailCampaignServiceBase
{
    abstract getAll(): Observable<Campaign[]>;
    abstract getById(id: number): Observable<Campaign>;
    abstract create(campaign: CampaignCreateDto): Observable<Campaign>;
    abstract update(id: number, updateDto: CampaignUpdateDto): Observable<void>;
    abstract delete(id: number): Observable<void>;
}

@Injectable({ providedIn: 'root' })
export class EmailCampaignService extends EmailCampaignServiceBase
{
    constructor
    (
        private _store: Store,
        private _api: ApiService,
        private _messages: MessageService,
        @Inject(BASE_URL) private _baseUrl: string,
    )
    {
        super();
    }

    override getAll(): Observable<Campaign[]>
    {
        return this._api.makeAPIGetCall<Campaign[]>(this._baseUrl, "/campaigns", {}, {})
            .pipe(
                map(campaigns => campaigns.map(campaign => Campaign.fromJSON(campaign)))
            );
    }

    override getById(id: number): Observable<Campaign>
    {
        return this._api.makeAPIGetCall<Campaign>(this._baseUrl, "/campaigns/" + id.toString(), {}, {})
            .pipe(
                map(campaign => Campaign.fromJSON(campaign))
            );
    }

    override create(campaign: CampaignCreateDto): Observable<Campaign>
    {
        return this._api.makeAPIPostCall<Campaign>(this._baseUrl, "/campaigns", campaign, {}, {})
            .pipe(
                map(createdCampaign =>
                {
                    const mappedCampaign = Campaign.fromJSON(createdCampaign);
                    this._store.dispatch(new CampaignStateActions.CampaignCreated({ campaign: mappedCampaign }));
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Campaign Created' });
                    return mappedCampaign;
                })
            );
    }

    override update(id: number, updateDto: CampaignUpdateDto): Observable<void>
    {
        return this._api.makeAPIPatchCall<void>(this._baseUrl, "/campaigns/" + id.toString(), updateDto, {}, {})
            .pipe(
                map(() =>
                {
                    this._store.dispatch(new CampaignStateActions.CampaignUpdated({ id, updateDto }));
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Campaign Updated' });
                    return;
                })
            );
    }

    override delete(id: number): Observable<void>
    {
        return this._api.makeAPIDeleteCall<void>(this._baseUrl, "/campaigns/" + id.toString(), {}, {})
            .pipe(
                map(() =>
                {
                    this._store.dispatch(new CampaignStateActions.CampaignDeleted({ id }));
                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Campaign Deleted' });
                    return;
                })
            );
    }
}
